<template>
  <div class="comment-wrapper">
    <div class="comment-container">
      <div class="list-comments">
        <div class="font-weight-bold text-h6">{{ $t('Comments') }}</div>
        <div class="comments">
          <template v-if="comments.length === 0">
            <div class="nodata">{{ $t('No comment') }}</div>
          </template>
          <template v-else>
            <div
              v-for="comment in comments"
              :key="comment.id"
              class="comment"
              :class="{ right: comment.user_id === userId }"
            >
              <div class="meta">
                <strong>{{ comment.user.name }}</strong>
                <span class="time">{{
                  utcToLocalDateTimeLess(comment.created_at)
                }}</span>
              </div>

              <div class="bubble">
                {{ comment.content }}
              </div>
              <div class="files">
                <single-file-item
                  v-for="file in comment.files"
                  :key="`file-${file.id}`"
                  :file="file"
                />
              </div>
            </div>
          </template>
        </div>
      </div>
    </div>
    <div>
      <alert />
      <v-form ref="commentForm" v-model="valid" lazy-validation>
        <div class="mb-4">
          <v-textarea
            v-model="form.content"
            :rules="rules.content"
            hide-details="auto"
            required
            outlined
            rows="3"
            dense
            :label="$t('Your comment')"
          />
        </div>
        <div class="fileAndSubmit">
          <div class="flex-1">
            <v-file-input
              v-model="form.files_upload"
              accept=".doc,.docx,.pdf,.xls,.xlsx,.ppt,.pptx,.txt,.jpg,.jpeg,.png,.gif"
              counter
              show-size
              :label="$t('Select your files')"
              prepend-icon=""
              append-icon="mdi-paperclip"
              outlined
              hide-details="auto"
              dense
              multiple
              :rules="rules.document"
            >
              <template v-slot:selection="{ index, text }">
                <v-chip v-if="index < 2" label small>
                  {{ text }}
                </v-chip>
              </template>
            </v-file-input>
          </div>
          <v-btn color="primary" :submitted="submitted" @click="submit">{{
            $t('Submit')
          }}</v-btn>
        </div>
      </v-form>
    </div>
  </div>
</template>

<script>
import validation from '@utils/validation'
import jsonToFormData from 'json-form-data'
import { get, call } from 'vuex-pathify'
import { utcToLocalDateTimeLess } from '@utils'
import Alert from '@/components/Alert'
import SingleFileItem from '@/components/SingleFileItem'

export default {
  name: 'Comment',
  components: {
    Alert,
    SingleFileItem
  },
  props: {
    objId: {
      type: Number,
      required: true
    },
    obj: {
      type: String,
      required: true
    }
  },
  computed: {
    userId: get('user/id'),
    comments: get('comment/comments')
  },
  data() {
    return {
      isNewComment: false,
      valid: true,
      submitted: false,
      form: {
        content: '',
        files_upload: []
      },
      rules: {
        content: [
          (v) => validation.required(v),
          (v) => validation.maxlength(v, 1000)
        ],
        document: [
          (v) => validation.accept(v),
          (v) => validation.arrayMax(v, 5),
          (v) => validation.filesize(v, 10 * 1024 * 1024)
        ]
      }
    }
  },
  mounted() {
    this.init()
  },
  unmounted() {
    this.hideAlert()
  },
  methods: {
    hideAlert: call('alert/hide'),
    loading: call('app/setLoading'),
    getComments: call('comment/getComments'),
    createComment: call('comment/createComment'),
    utcToLocalDateTimeLess,
    async submit(e) {
      e.preventDefault()
      const valid = this.$refs.commentForm.validate()
      if (!valid) {
        alert(this.$t('Please check data before submit!'))
      } else {
        const data = {
          obj_id: this.objId,
          obj: this.obj,
          ...this.form
        }
        this.hideAlert()
        this.loading(true)
        const formData = jsonToFormData(data)
        const status = await this.createComment(formData)
        this.loading(false)
        this.scrollToBottom()
        if (status) {
          this.$refs.commentForm.reset()
          this.isNewComment = false
        }
      }
      return false
    },
    async init() {
      this.loading(true)
      await this.getComments({ obj_id: this.objId, obj: this.obj })
      setTimeout(() => {
        this.loading(false)
        this.scrollToBottom()
      }, 0)
    },
    scrollToBottom() {
      this.$nextTick(() => {
        const container = this.$el.querySelector('.list-comments')
        container.scrollTop = container.scrollHeight
      })
    }
  }
}
</script>

<style scoped lang="scss">
.comment-wrapper {
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  box-sizing: border-box;
  gap: 16px;

  .comment-container {
    flex: 1;
    overflow: hidden;
    .list-comments {
      height: 100%;
      max-height: 100%;
      overflow-y: auto;
    }
  }
  .fileAndSubmit {
    display: flex;
    gap: 12px;
    align-items: flex-start;
    .flex-1 {
      flex: 1;
    }
  }
  .comments {
    display: flex;
    flex-direction: column;
    gap: 12px;
    color: #000;
    padding: 0 8px;
  }

  .nodata {
    text-align: center;
    padding: 16px;
    width: 100%;
    font-size: 16px;
  }

  .comment {
    max-width: 80%;
    display: flex;
    flex-direction: column;
    align-self: flex-start;

    .meta {
      font-size: 12px;
      color: #666;
      margin-bottom: 2px;

      .time {
        margin-left: 6px;
      }
    }

    .bubble {
      padding: 6px 10px;
      border-radius: 6px;
      background: rgba($color: #eee, $alpha: 0.4);
      color: #000;
      line-height: 1.4;
      word-break: break-word;
      width: fit-content;
    }

    &.right {
      align-self: flex-end;
      text-align: right;

      .bubble {
        margin-left: auto;
      }
    }
  }
}
.theme--dark {
  .bubble {
    background: #333 !important;
    color: #fff !important;
  }
}
</style>
